import { dayjs } from "@/utils/date";

/**
 * Human label for a cancellation timestamp, used by the "Cancelada …"
 * pill on reservation cards.
 *
 * Unified "relative → fecha" so it never reads ambiguously (an earlier
 * version showed a weekday abbreviation — "mar." reads as both *martes*
 * and *marzo* in Spanish). Buckets, in order:
 *
 *   < 1 min        → "Cancelada hace un momento"
 *   < 60 min       → "Cancelada hace Xm"
 *   same calendar day → "Cancelada hoy · 6:05 PM"
 *   previous day   → "Cancelada ayer · 8:17 AM"
 *   older          → "Cancelada 30 may · 6:05 PM"
 *
 * Today/yesterday are decided by calendar day (not elapsed hours) so the
 * result is predictable across midnight. Falls back to a bare
 * "Cancelada" when there is no usable timestamp.
 */
export function formatCancelledPill(cancelledAt: string | null | undefined): string {
  if (!cancelledAt) return "Cancelada";
  const at = dayjs(cancelledAt);
  if (!at.isValid()) return "Cancelada";
  const now = dayjs();
  const diffMinutes = now.diff(at, "minute");
  if (diffMinutes < 1) return "Cancelada hace un momento";
  if (diffMinutes < 60) return `Cancelada hace ${diffMinutes}m`;
  const time = at.format("h:mm A");
  if (at.isSame(now, "day")) return `Cancelada hoy · ${time}`;
  if (at.isSame(now.subtract(1, "day"), "day")) return `Cancelada ayer · ${time}`;
  return `Cancelada ${at.format("D MMM")} · ${time}`;
}
